import { useEffect, useState } from "react";
import { Display, TableProps, Table } from "@apptronik/react-exoskeleton";
import { Button, Box, Chip } from "@mui/material"; 
import { AddRoleDialog } from "./AddRoleDialog";
import { EditPermissionsDialog } from "./EditPermissionsDialog";
import query_data from "../../../js/query_data";
import { isAdmin } from "../../../js/auth_helpers";

type Columns = {
    "role": string;
    "permissions": React.ReactNode; 
    "edit"?: React.ReactNode; // Changed to ReactNode to allow Box wrapper
    "delete"?: React.ReactNode; // Changed to ReactNode to allow Box wrapper
}

export function difference<T>(arrayA: T[], arrayB: T[]): T[] {
    const setB = new Set(arrayB);
    return arrayA.filter(item => !setB.has(item));
}

export default function RolePage() {
    const [roles, setRoles] = useState([])
    const [openNewRole, setOpenNewRole] = useState<boolean>(false);
    const [openEditPermissions, setOpenEditPermissions] = useState<boolean>(false);
    const [roleName, setSelectedRoleName] = useState<string>('');
    const [selectedPermissions, setPermissionsForSelectedRole] = useState([]);
    const [allPermissions, setAllPermissions] = useState([])
    const [self, setSelf] = useState(null);

    const userIsAdmin = (): boolean => {
        return isAdmin(self);
    }

    const handleOpen = () => setOpenNewRole(true);
    const handleClose = () => setOpenNewRole(false);

    const handleSubmitNewRole = async (selectedRole, requestedPermissions) => {
        await query_data(`/roles/${selectedRole}`, requestedPermissions);
        setOpenNewRole(false);
    };
    
    const handleOpenPermissions = (name, permissions) => {
        setSelectedRoleName(name)
        setPermissionsForSelectedRole(permissions)
        setOpenEditPermissions(true);
    };

    const handleCancelPermissionsEdit = () => {
        setSelectedRoleName('');
        setPermissionsForSelectedRole([])
        setOpenEditPermissions(false);
    };

    const handleSubmitPermissions = async (selectedRole, requestedPermissions) => {
        const role = roles.find((element) => element.name === selectedRole);
        if (!role) return;

        const newPermissions = difference(requestedPermissions, selectedPermissions);
        const removedPermissions = difference(selectedPermissions, requestedPermissions);

        const addPromises = newPermissions.map(perm =>
            query_data(`/roles/permissions/${role.id}`, perm)
        );

        const removePromises = removedPermissions.map(perm =>
            query_data(`/roles/permissions/${role.id}`, perm, 'DELETE')
        );

        await Promise.all([...addPromises, ...removePromises]);
        setOpenEditPermissions(false);
    };

    const handleDeleteRole = async (role) => {
        await query_data(`/roles/${role.id}`, null, 'DELETE');
        fetchRoles();
    };
    
    const fetchRoles = async () => {
        const data = await query_data('/roles', {});
        setRoles(data);
    };

    useEffect(() => {
        fetchRoles();
    }, [openNewRole, openEditPermissions]);

    useEffect(() => {
        const fetchPermissions = async () => {
            const data = await query_data('/roles/permissions', {});
            setAllPermissions(data.sort());
        };
        const fetchSelf = async () => {
            const data = await query_data('/users/get_self', {});
            setSelf(data[0]);
        };
        fetchPermissions();
        fetchSelf();
    }, []);

    const rows = roles.map((peep) => {
        const permissions = (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {peep.permissions?.map((element) => (
                    <Chip label={element} key={element} size="small" variant="outlined" />
                ))}
            </Box>
        );

        const rowsConfig = {
            link: ``,
            cells: {
                role: peep.name ?? 'N/A',
                permissions: permissions,
            }
        };

        if (userIsAdmin()) {
            // 💅 Wrap buttons in a Box to set a minimum width for the column cell
            rowsConfig.cells["edit"] = (
                <Box sx={{ minWidth: '80px' }}>
                    <Button size="small" onClick={() => handleOpenPermissions(peep.name, peep.permissions)}>
                        Edit
                    </Button>
                </Box>
            );
            rowsConfig.cells["delete"] = (
                <Box sx={{ minWidth: '120px' }}>
                    <Button size="small" onClick={() => handleDeleteRole(peep)}>
                        Delete
                    </Button>
                </Box>
            );
        }
        return rowsConfig;
    });

    // 🕵️‍♂️ Added unsortableColumns to disable sorting on specific columns
    const TableInput: TableProps<Columns> = {
        rows,
    };

    return (
        <Box sx={{ padding: '2.5rem' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Display variant='medium'>
                    Roles
                </Display>
                <Button variant="contained" onClick={handleOpen}>
                    Add Role
                </Button>
            </Box>

            <AddRoleDialog
                open={openNewRole}
                handleClose={handleClose}
                handleSubmit={handleSubmitNewRole}
                permissionsOptions={allPermissions}
            />
            <EditPermissionsDialog
                open={openEditPermissions}
                handleCancel={handleCancelPermissionsEdit}
                handleSubmit={handleSubmitPermissions}
                roleName={roleName}
                permissionsOptions={allPermissions}
                existingPermissions={selectedPermissions}
            />

            {rows.length > 0 && <Table {...TableInput} />}
        </Box>
    );
}